import React, { useState, useEffect } from "react";
import { Quote, ChevronDown } from "lucide-react";
import { clampSentence } from "../lib/textUtils";

const STORAGE_KEY = "tourismintel_brief_seen";

const BriefOfWeek = ({ brief, darkMode }) => {
  const dm = darkMode;

  // First visit: open. After user has clicked once OR a brief has already been
  // seen by this client: closed. We persist the "seen" flag in localStorage.
  const [open, setOpen] = useState(() => {
    try {
      return !window.localStorage.getItem(STORAGE_KEY);
    } catch {
      return true; // SSR / private mode → default open
    }
  });

  // Mark as seen when the brief renders open the first time, so next visit collapses.
  useEffect(() => {
    if (!brief?.content || !open) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch { /* private mode — best effort */ }
  }, [brief?.content, open]);

  if (!brief?.content) return null;

  // P0 iter90: auto-hide when stale (>14 days) — old intelligence on the
  // homepage hurts credibility. Backend regenerates via the stale-brief guard.
  const briefTs = brief.updated_at || brief.generated_at;
  const isStale = briefTs
    ? (Date.now() - new Date(briefTs).getTime()) > 14 * 24 * 3600 * 1000
    : false;
  if (isStale) return null;

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";
  // Collapsed preview ends at a sentence boundary — never mid-sentence (item 3)
  const preview = clampSentence(brief.content, 160);

  return (
    <div data-testid="brief-of-week" className={`rounded-2xl mb-8 overflow-hidden ${dm ? "bg-[#1a2122]" : "bg-white"}`}
      style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.12), 0 4px 24px rgba(25,28,29,0.04)", borderLeft: `4px solid ${dm ? "#22d3ee" : "#006874"}` }}>
      {/* Always-visible header — clickable to toggle */}
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="brief-toggle-btn"
        aria-expanded={open}
        className={`w-full text-left transition-colors ${open ? "" : (dm ? "hover:bg-white/3" : "hover:bg-[#f8f9fa]")}`}
      >
        <div className="flex items-start gap-4 p-6">
          {brief.author_photo_url ? (
            <img src={brief.author_photo_url} alt={brief.author_name || "Mirko Lalli"}
              className="w-14 h-14 rounded-full object-cover flex-shrink-0 ring-2"
              style={{ ringColor: dm ? "#22d3ee" : "#006874" }} />
          ) : (
            <div className={`w-14 h-14 rounded-full flex-shrink-0 flex items-center justify-center font-black text-lg ${dm ? "bg-cyan-400/15 text-cyan-400" : "bg-[#006874]/10 text-[#006874]"}`}
              style={{ fontFamily: "Newsreader, serif" }}>ML</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`category-tag ${dm ? "text-cyan-400" : "text-[#006874]"}`}>Brief of the Week</span>
              <Quote className={`w-3 h-3 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`} />
            </div>
            <p className={`editorial-heading text-base font-semibold leading-tight ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{brief.author_name || "Mirko Lalli"}</p>
            <p className={`category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>AI &amp; Tourism Expert · {fmtDate(brief.updated_at || brief.generated_at)}</p>
            {/* Inline preview when collapsed */}
            {!open && (
              <p className={`mt-2 text-sm italic line-clamp-2 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}
                style={{ fontFamily: "'Newsreader', serif" }}>
                {preview}
              </p>
            )}
          </div>
          <ChevronDown
            data-testid="brief-chevron"
            className={`w-5 h-5 flex-shrink-0 mt-1 transition-transform duration-300 ${open ? "rotate-180" : ""} ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}
          />
        </div>
      </button>

      {/* Collapsible body */}
      {open && (
        <div data-testid="brief-body" className="px-6 pb-6 -mt-2 animate-fade-in">
          {/* Full quote — italic serif 19px, never truncated (item 3, tier 1) */}
          <p className={`leading-relaxed ${dm ? "text-slate-300" : "text-[#3b494c]"}`}
            style={{ fontFamily: "'Newsreader', serif", fontSize: 19, lineHeight: 1.7, fontStyle: "italic" }}>
            {brief.content}
          </p>
          <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${dm ? "#2a3435" : "#e8ecee"}` }}>
            <a href="/travel-brief" data-testid="read-full-brief-link"
              className={`category-tag font-bold transition-colors ${dm ? "text-cyan-400 hover:text-cyan-300" : "text-[#006874] hover:text-[#004f58]"}`}>
              Read the full brief →
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default BriefOfWeek;
